import React, { useLayoutEffect, useRef } from 'react';

import Checkers from '../helpers/class/Checkers';

/**
 * Renders admin-authored raw HTML that may contain <script> tags (e.g. TripAdvisor/Booking.com
 * widget embeds). React's dangerouslySetInnerHTML never executes injected <script> tags (browsers
 * treat them as inert when set via innerHTML), so widgets like:
 *   <div id="TA_excellent..."></div><script async src="https://www.jscache.com/wejs?...">
 * would render the empty placeholder div but never run the script that fills it in.
 *
 * The initial render still uses dangerouslySetInnerHTML (matched on both server and client) so the
 * markup - including the <script> tag - is part of the page's actual server-rendered HTML. That way
 * the browser's native HTML parser starts fetching the widget's script immediately, in parallel with
 * everything else, instead of waiting for React to hydrate and run an effect first (which noticeably
 * delayed widgets like the TripAdvisor badge). Scripts placed this way by the browser's own parser
 * execute normally; it's only script tags inserted afterwards via the innerHTML DOM API that browsers
 * treat as inert - which is what the effect below re-creates, but only when `props.html` changes after
 * that first render (e.g. a client-side navigation swaps in a different badge).
 */
export default function RawEmbedHtml ( props ) {
  const ref = useRef<HTMLDivElement>( null );
  const isFirstRun = useRef( true );

  // useLayoutEffect (not useEffect) - runs synchronously right after the DOM
  // mutates and before the browser paints, so the widget's own script starts
  // its network fetch (TripAdvisor/Booking.com widget content) one frame
  // earlier than it otherwise would.
  useLayoutEffect( () => {
    const container = ref.current;
    if ( container == null || !Checkers.isValidString( props.html ) ) {
      return;
    }

    container.style.display = '';

    // Skip re-injecting on the first run: this markup was already rendered via
    // dangerouslySetInnerHTML below (server + initial client render), so its
    // <script> tag already ran as part of the browser's native parse. Re-creating
    // it here would just restart the widget's request.
    if ( !isFirstRun.current ) {
      container.innerHTML = props.html;

      const scripts = Array.from( container.querySelectorAll( 'script' ) );
      scripts.forEach( ( oldScript ) => {
        const newScript = document.createElement( 'script' );
        Array.from( oldScript.attributes ).forEach( ( attr ) => {
          newScript.setAttribute( attr.name, attr.value );
        } );
        newScript.text = oldScript.textContent || '';
        oldScript.parentNode?.replaceChild( newScript, oldScript );
      } );
    }
    isFirstRun.current = false;

    // Lazy images (the Lodge site loads them with a lazyload script): the real url is in data-src and
    // src is a 1px placeholder, so show the real image directly.
    Array.from( container.querySelectorAll( 'img[data-src]' ) ).forEach( ( lazyImage ) => {
      const dataSrc = lazyImage.getAttribute( 'data-src' );
      if ( Checkers.isValidString( dataSrc ) ) {
        lazyImage.setAttribute( 'src', dataSrc );
      }
      const dataSrcset = lazyImage.getAttribute( 'data-srcset' );
      if ( Checkers.isValidString( dataSrcset ) ) {
        lazyImage.setAttribute( 'srcset', dataSrcset );
      }
    } );

    // hideIfImageFails: for embeds that are just an <img> (e.g. award/review
    // badges), hide the whole slot - not just the broken-image icon - when
    // there's no image at all, or the image URL is dead.
    // Only embeds that are nothing but images are hidden when the image is missing. An embed with other content
    // (an iframe badge, links with text, ...) is shown.
    const contentWithoutScripts = container.cloneNode( true ) as HTMLElement;
    contentWithoutScripts.querySelectorAll( 'script, style' ).forEach( ( node ) => node.remove() );
    const hasOtherContent = container.querySelector( 'iframe, video, object, embed, svg' ) != null
      || ( contentWithoutScripts.textContent || '' ).replace( /\s+/g, '' ) !== '';

    if ( props.hideIfImageFails && !hasOtherContent ) {
      const images = Array.from( container.querySelectorAll( 'img' ) ) as HTMLImageElement[];
      if ( images.length === 0 ) {
        container.style.display = 'none';
      } else {
        images.forEach( ( img ) => {
          if ( !Checkers.isValidString( img.getAttribute( 'src' ) ) ) {
            container.style.display = 'none';
            return;
          }
          img.addEventListener( 'error', () => {
            container.style.display = 'none';
          } );
        } );
      }
    }
  }, [ props.html ] );

  return (
      <div
          className={props.className}
          ref={ref}
          suppressHydrationWarning
          dangerouslySetInnerHTML={{ __html: props.html }}
      />
  );
}
